import { create } from 'zustand';
import { QuickRateResult } from "@sdk/shared/types/order.types";

interface QuickTradeRateStore {
  // Rate data
  currentRate: number | null;
  manualRate: number | null;
  useManualRate: boolean;
  quickRateData: QuickRateResult | null;

  // Rate calculation state
  isLoading: boolean;
  hasError: boolean;
  errorMessage: string | null;
  noAds: boolean;
  adsCount: number;

  // Rate tracking
  hasInitialRate: boolean;
  needsReestimate: boolean;
  lastSearchParams: string;

  // Rate actions
  setCurrentRate: (rate: number | null) => void;
  setManualRate: (rate: number | null) => void;
  setUseManualRate: (useManual: boolean) => void;
  setQuickRateData: (data: QuickRateResult | null) => void;

  // Loading and error actions
  setIsLoading: (loading: boolean) => void;
  setHasError: (hasError: boolean) => void;
  setErrorMessage: (message: string | null) => void;
  setNoAds: (noAds: boolean) => void;
  setAdsCount: (count: number) => void;

  // Rate tracking actions
  setHasInitialRate: (hasInitial: boolean) => void;
  setNeedsReestimate: (needs: boolean) => void;
  setLastSearchParams: (params: string) => void;

  // Rate calculation actions
  updateRate: (rateData: QuickRateResult | null, hasError: boolean, errorMessage?: string) => void;
  handleRateChange: (newRate: number, isManual: boolean) => void;
  requestReestimate: () => void;

  // Utility actions
  getEffectiveRate: () => number | null;
  shouldShowReestimate: () => boolean;
  getRateDisplayInfo: () => {
    rate: number | null;
    isManual: boolean;
    isLoading: boolean;
    hasError: boolean;
    errorMessage: string | null;
  };

  // Reset actions
  resetRate: () => void;
  resetAll: () => void;
}

export const useQuickTradeRateStore = create<QuickTradeRateStore>((set, get) => ({
  // Initial rate data
  currentRate: null,
  manualRate: null,
  useManualRate: false,
  quickRateData: null,

  // Initial rate calculation state
  isLoading: false,
  hasError: false,
  errorMessage: null,
  noAds: false,
  adsCount: 0,

  // Initial rate tracking
  hasInitialRate: false,
  needsReestimate: false,
  lastSearchParams: '',

  // Rate actions
  setCurrentRate: rate => set({ currentRate: rate }),
  setManualRate: rate => set({ manualRate: rate }),
  setUseManualRate: useManual => set({ useManualRate: useManual }),
  setQuickRateData: data => set({ quickRateData: data }),

  // Loading and error actions
  setIsLoading: loading => set({ isLoading: loading }),
  setHasError: hasError => set({ hasError }),
  setErrorMessage: message => set({ errorMessage: message }),
  setNoAds: noAds => set({ noAds }),
  setAdsCount: count => set({ adsCount: count }),

  // Rate tracking actions
  setHasInitialRate: hasInitial => set({ hasInitialRate: hasInitial }),
  setNeedsReestimate: needs => set({ needsReestimate: needs }),
  setLastSearchParams: params => set({ lastSearchParams: params }),

  // Rate calculation actions
  updateRate: (rateData, hasError, errorMessage) => {
    if (hasError) {
      set({
        currentRate: null,
        noAds: true,
        hasError: true,
        errorMessage: errorMessage || 'No ads found',
        isLoading: false,
        quickRateData: null,
      });
    } else if (rateData && 'rate' in rateData && rateData.rate !== undefined) {
      set({
        currentRate: rateData.rate,
        noAds: false,
        hasError: false,
        errorMessage: null,
        isLoading: false,
        quickRateData: rateData,
        hasInitialRate: true,
        needsReestimate: false,
        adsCount: 'numAds' in rateData ? rateData.numAds ?? 0 : 0,
      });
    }
  },

  handleRateChange: (newRate, isManual) => {
    if (isManual) {
      set({
        manualRate: newRate,
        useManualRate: true,
      });
    } else {
      set({
        useManualRate: false,
        manualRate: null,
      });
    }
  },

  requestReestimate: () => {
    set({
      currentRate: null,
      noAds: false,
      needsReestimate: false,
      isLoading: true,
      hasError: false,
      errorMessage: null,
    });
  },

  // Utility actions
  getEffectiveRate: () => {
    const { useManualRate, manualRate, currentRate } = get();
    return useManualRate ? manualRate : currentRate;
  },

  shouldShowReestimate: () => {
    const { needsReestimate, currentRate } = get();
    return needsReestimate && currentRate !== null;
  },

  getRateDisplayInfo: () => {
    const { currentRate, useManualRate, manualRate, isLoading, hasError, errorMessage } = get();
    return {
      rate: useManualRate ? manualRate : currentRate,
      isManual: useManualRate,
      isLoading,
      hasError,
      errorMessage,
    };
  },

  // Reset actions
  resetRate: () =>
    set({
      currentRate: null,
      manualRate: null,
      useManualRate: false,
      quickRateData: null,
      isLoading: false,
      hasError: false,
      errorMessage: null,
      noAds: false,
      adsCount: 0,
      needsReestimate: false,
    }),

  resetAll: () => {
    get().resetRate();
    set({
      hasInitialRate: false,
      lastSearchParams: '',
    });
  },
}));
